import { useState, useEffect, useCallback, useRef } from 'react';
import axios, { API } from '@/utils/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API_BASE = API.replace('/api', '');

const PromoBanner = () => {
  const { user, loading: authLoading } = useAuth();
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [globalSettings, setGlobalSettings] = useState({
    global_transition: 'slide',
    auto_rotate_interval: 5000
  });
  const trackedImpressions = useRef(new Set()); // Track which banners we've logged impressions for

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/banners/settings`);
      setGlobalSettings(res.data);
    } catch (err) {
      // Silently fail - use defaults
      console.debug('Banner settings fetch failed, using defaults');
    }
  }, []);

  // Track banner impression (when banner is shown)
  const trackImpression = useCallback((bannerId) => {
    if (!bannerId || trackedImpressions.current.has(bannerId)) return;
    
    trackedImpressions.current.add(bannerId);
    
    // Fire and forget - don't await
    axios.post(`${API_BASE}/api/banners/track`, {
      banner_id: bannerId,
      event_type: 'impression'
    }).catch(() => {
      // Silently fail - tracking is non-critical
    });
  }, []);

  // Track banner click
  const trackClick = useCallback((bannerId) => {
    if (!bannerId) return;
    
    // Fire and forget - don't block navigation
    axios.post(`${API_BASE}/api/banners/track`, {
      banner_id: bannerId,
      event_type: 'click'
    }).catch(() => {
      // Silently fail - tracking is non-critical
    });
  }, []);

  const fetchBanners = useCallback(async () => {
    // Wait for auth to be ready
    if (authLoading) return;
    
    try {
      // Use user from AuthContext instead of making a separate /auth/me call
      // B2B users get their client's banners, regular users get admin banners
      const endpoint = user?.client_id 
        ? `${API_BASE}/api/client-banners/active`
        : `${API_BASE}/api/banners`;
      
      const res = await axios.get(endpoint, { withCredentials: true });
      // Filter only active banners and sort by display_order
      const activeBanners = (res.data.banners || [])
        .filter(b => b.is_active)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      
      if (activeBanners.length > 0) {
        setBanners(activeBanners);
      } else {
        // No active banners - show nothing
        setBanners([]);
      }
    } catch (err) {
      // Silently fail - banners are non-critical
      console.debug('Banners fetch failed:', err.message);
      // On error, show nothing rather than broken image
      setBanners([]);
    } finally {
      setLoading(false);
    }
  }, [authLoading, user?.client_id]);

  // Fetch banners and settings when auth is ready
  useEffect(() => {
    if (!authLoading) {
      fetchBanners();
      fetchSettings();
    }
  }, [authLoading, fetchBanners, fetchSettings]);

  // Track impression when banner changes
  useEffect(() => {
    if (banners.length > 0 && banners[currentIndex]?.id) {
      trackImpression(banners[currentIndex].id);
    }
  }, [currentIndex, banners, trackImpression]);

  // Auto-rotate banners based on global settings
  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, globalSettings.auto_rotate_interval);
    
    return () => clearInterval(interval);
  }, [banners.length, globalSettings.auto_rotate_interval]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  }, [banners.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  }, [banners.length]);
  
  // Get the transition effect for current banner (per-banner or global)
  const getTransitionEffect = () => {
    const banner = banners[currentIndex];
    return banner?.transition || globalSettings.global_transition || 'slide';
  };

  if (loading) {
    return (
      <div 
        className="mt-6 rounded-xl overflow-hidden h-32 animate-pulse"
        style={{ backgroundColor: 'var(--theme-background-secondary)' }}
      />
    );
  }

  if (banners.length === 0) {
    return null;
  }

  const currentBanner = banners[currentIndex];
  const transitionEffect = getTransitionEffect();
  
  // Get transition CSS classes - simplified for smooth animation
  const getTransitionClasses = () => {
    switch (transitionEffect) {
      case 'fade':
        return 'transition-opacity duration-500 ease-in-out';
      case 'zoom':
        return 'transition-all duration-500 ease-in-out';
      case 'flip':
        return 'transition-transform duration-500 ease-in-out';
      case 'none':
        return '';
      case 'slide':
      default:
        return 'transition-all duration-500 ease-in-out';
    }
  };
  
  // Construct full image URL - handle both relative and absolute URLs
  const getImageUrl = (url) => {
    if (!url) return '/unclonedcommunity.png';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${API_BASE}${url}`;
    return url;
  };
  
  // Normalize link URLs - add https:// if no protocol specified
  const normalizeUrl = (url) => {
    if (!url) return '#';
    // Already has protocol
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    // Relative path (starts with /)
    if (url.startsWith('/')) return url;
    // Add https:// for URLs like example.com or www.example.com
    return `https://${url}`;
  };
  
  // Get appropriate image URL based on screen size
  const getBannerImageUrl = (banner) => {
    // Check if we're on mobile (< 640px)
    const isMobile = window.innerWidth < 640;
    if (isMobile && banner.mobile_image_url) {
      return getImageUrl(banner.mobile_image_url);
    }
    return getImageUrl(banner.image_url);
  };

  return (
    <div className="relative mt-6 mb-6 group overflow-hidden">
      {/* Banner Image */}
      <a
        href={normalizeUrl(currentBanner.link_url)}
        target="_blank"
        rel="noopener noreferrer"
        className={`block rounded-xl overflow-hidden hover:scale-[1.02] ${getTransitionClasses()}`}
        style={{ 
          boxShadow: '0 10px 25px rgba(var(--theme-primary-rgb), 0.3)',
          minHeight: '100px'
        }}
        data-testid="promo-banner"
        onClick={() => trackClick(currentBanner.id)}
      >
        {/* Desktop Image */}
        <img
          key={`desktop-${currentBanner.id || currentIndex}`}
          src={getImageUrl(currentBanner.image_url)}
          alt={currentBanner.alt_text || 'Promotional Banner'}
          className={`w-full h-auto object-cover transition-opacity duration-300 ${currentBanner.mobile_image_url ? 'hidden sm:block' : ''}`}
          style={{ opacity: 1 }}
          onLoad={(e) => { e.target.style.opacity = '1'; }}
          onError={(e) => {
            // Hide banner if image fails to load
            console.warn('Banner image failed to load:', currentBanner.image_url);
            e.target.style.display = 'none';
          }}
        />
        {/* Mobile Image (if available) */}
        {currentBanner.mobile_image_url && (
          <img
            key={`mobile-${currentBanner.id || currentIndex}`}
            src={getImageUrl(currentBanner.mobile_image_url)}
            alt={currentBanner.alt_text || 'Promotional Banner'}
            className="w-full h-auto object-cover block sm:hidden transition-opacity duration-300"
            style={{ opacity: 1 }}
            onLoad={(e) => { e.target.style.opacity = '1'; }}
            onError={(e) => {
              // Fall back to desktop image on error
              e.target.style.display = 'none';
              const desktopImg = e.target.previousSibling;
              if (desktopImg) {
                desktopImg.classList.remove('hidden');
                desktopImg.classList.remove('sm:block');
              }
            }}
          />
        )}
      </a>

      {/* Navigation arrows (only if multiple banners) */}
      {banners.length > 1 && (
        <>
          <button
            onClick={(e) => { e.preventDefault(); goToPrev(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ backgroundColor: 'rgba(var(--theme-background-rgb), 0.8)' }}
            aria-label="Previous banner"
          >
            <ChevronLeft className="w-5 h-5" style={{ color: 'var(--theme-text)' }} />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); goToNext(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ backgroundColor: 'rgba(var(--theme-background-rgb), 0.8)' }}
            aria-label="Next banner"
          >
            <ChevronRight className="w-5 h-5" style={{ color: 'var(--theme-text)' }} />
          </button>

          {/* Dots indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {banners.map((banner, index) => (
              <button
                key={banner.id || `banner-dot-${index}`}
                onClick={(e) => { e.preventDefault(); setCurrentIndex(index); }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'w-4' 
                    : 'opacity-50 hover:opacity-75'
                }`}
                style={{ 
                  backgroundColor: index === currentIndex 
                    ? 'var(--theme-primary)' 
                    : 'rgba(var(--theme-text-rgb, 255, 255, 255), 0.5)'
                }}
                aria-label={`Go to banner ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PromoBanner;
